// BCM-898 · Goods Receipt Dashboard — Action-section dispatcher.
// Resolves the pending action for a batch (from the row + lazily-loaded A2 detail)
// and mounts the matching PA component. Computes the view-only fallback (doc rev V2)
// per action from featuresAccess. Each PA lives in its own file under ../actions/.
import React, { useEffect } from "react";
import { CircularProgress, Box, Typography } from "@material-ui/core";
import { resolvePendingAction } from "../utils/resolvePendingAction";
import { PA } from "../utils/constants";
import { usePreIBDGRNGate } from "../hooks/usePreIBDGRNGate";

import PA1PostMfgDocuments from "../actions/PA1PostMfgDocuments";
import PA2ReviewFields from "../actions/PA2ReviewFields";
import PA3ReviewConsumption from "../actions/PA3ReviewConsumption";
import PA4MaterialExpired from "../actions/PA4MaterialExpired";
import PA5MaterialNotInBoM from "../actions/PA5MaterialNotInBoM";
import PA6VarianceMismatch from "../actions/PA6VarianceMismatch";
import PA7PreShipmentDocuments from "../actions/PA7PreShipmentDocuments";
import PA8PreIBDGRNCheck from "../actions/PA8PreIBDGRNCheck";
import PA9GenerateGRNIBD from "../actions/PA9GenerateGRNIBD";
import PA10QAReleasePending from "../actions/PA10QAReleasePending";
import PA11QAReleaseRejected from "../actions/PA11QAReleaseRejected";
import PA12PostShipmentDocuments from "../actions/PA12PostShipmentDocuments";

// PA9 gate: the workflow flag (showGenerateGRNActionButton /
// showCreateIBDActionButton) says the batch is ready to generate, but the
// Pre-IBD/Pre-GRN checks (the same ones PA8 runs) must still all pass before
// the Generate screen is shown — while any check is failing, PA8's failure
// screen keeps showing instead of PA9's Generate button (deck: "New logic
// change" note on the Pre-GRN Check Failed slide).
//
// resolvePendingAction (label-free, synchronous) has no way to know this
// outcome ahead of the API call, so the collapsed row's label would otherwise
// keep reading "IBD generation pending" / "GRN generation pending" even while this renders PA8's
// failure screen. `onActionResolved` reports which PA actually got rendered
// so the caller (BatchRow) can correct the label it shows alongside.
const PA9Gate = ({ onActionResolved, ...props }) => {
  const { row, detail } = props;
  const { loading, allPassed } = usePreIBDGRNGate(row, detail);

  useEffect(() => {
    if (loading || !onActionResolved) return;
    onActionResolved(allPassed === false ? PA.PA8 : PA.PA9);
  }, [loading, allPassed, onActionResolved]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  return allPassed === false ? <PA8PreIBDGRNCheck {...props} /> : <PA9GenerateGRNIBD {...props} />;
};

const COMPONENTS = {
  [PA.PA1]: PA1PostMfgDocuments,
  [PA.PA2]: PA2ReviewFields,
  [PA.PA3]: PA3ReviewConsumption,
  [PA.PA4]: PA4MaterialExpired,
  [PA.PA5]: PA5MaterialNotInBoM,
  [PA.PA6]: PA6VarianceMismatch,
  [PA.PA7]: PA7PreShipmentDocuments,
  [PA.PA8]: PA8PreIBDGRNCheck,
  [PA.PA9]: PA9Gate,
  [PA.PA10]: PA10QAReleasePending,
  [PA.PA11]: PA11QAReleaseRejected,
  [PA.PA12]: PA12PostShipmentDocuments,
};

// Whether the current user has only VIEW access for a batch's pending action.
// CMO users are view-only across the whole dashboard; a BMS user is view-only on
// the action-bearing PAs (PA2/PA3/PA6/PA9) when they lack that feature. A
// view-only action still expands and renders (BCM-898 follow-up per FSD) — its
// section is shown with controls disabled instead of being omitted entirely.
export const isViewOnlyAccess = (pa, featuresAccess) => {
  if (localStorage.getItem("collabType") === "CMO") return true;
  const fa = featuresAccess || {};
  switch (pa) {
    case PA.PA2:
      return fa.reviewFields === false;
    case PA.PA3:
      return fa.approveFGConsumption === false;
    case PA.PA6:
      return fa.approveFGConsumption === false;
    case PA.PA9:
      return fa.generateGRN === false && fa.createIBD === false;
    default:
      return false;
  }
};

const ActionSection = ({ row, detail, detailLoading, featuresAccess, onRefresh, onActionResolved }) => {
  if (detailLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={3}>
        <CircularProgress size={24} />
        <Typography variant="body2" style={{ color: "#6B7280", marginLeft: 12 }}>
          Loading batch details…
        </Typography>
      </Box>
    );
  }

  // resolvePendingAction returns a single current action today (a batch has
  // exactly one pending action per the 2026-08-21 review); `pas` is kept as a
  // list so this dispatcher doesn't need to change if that is ever relaxed
  // (deck slide 20 shows multiple simultaneous approve panels within ONE
  // action — PA6 — which that component already handles internally).
  const { pas } = resolvePendingAction(row, detail);

  const sections = pas.filter((pa) => COMPONENTS[pa]);

  if (sections.length === 0) return null;

  return (
    <React.Fragment>
      {sections.map((pa) => {
        const Component = COMPONENTS[pa];
        return (
          <Component
            key={pa}
            row={row}
            detail={detail}
            featuresAccess={featuresAccess}
            viewOnly={isViewOnlyAccess(pa, featuresAccess)}
            onRefresh={onRefresh}
            {...(pa === PA.PA9 ? { onActionResolved } : {})}
          />
        );
      })}
    </React.Fragment>
  );
};

export default ActionSection;
